import { getCfg } from "./config";
import { withRequestDeadline } from "./network";
import { SessionExpiredError } from "./request";

// Framework-agnostic Cognito auth, ported verbatim from app.html (PKCE hosted-UI,
// direct InitiateAuth password/passkey, WebAuthn register/login, ForgotPassword).
// It talks to Cognito over TLS and stores tokens in sessionStorage exactly like
// the original, so nothing about the auth wire changes in the React rewrite.

let authGeneration = 0;
const pendingCredentials = new Set<AbortController>();
export const cancelAuthentication = (): void => {
  authGeneration++;
  for (const controller of pendingCredentials) controller.abort(new DOMException("Sign-in was cancelled", "AbortError"));
  pendingCredentials.clear();
};
async function credentialPrompt<T>(run: (signal: AbortSignal) => Promise<T>): Promise<T> {
  const controller = new AbortController();
  pendingCredentials.add(controller);
  try { return await withRequestDeadline(run, 60_000, controller.signal); }
  finally { pendingCredentials.delete(controller); }
}
export const currentAuthGeneration = (): number => authGeneration;
export const requireCurrentAuth = (generation: number): void => {
  if (generation !== authGeneration) throw new DOMException("Sign-in was cancelled", "AbortError");
};

export const emailRe = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// ── Cognito JSON surface (external, loosely typed at the boundary) ──────────
interface CognitoResult {
  AccessToken?: string;
  RefreshToken?: string;
}
interface CognitoResponse {
  __type?: string;
  message?: string;
  ChallengeName?: string;
  ChallengeParameters?: Record<string, string>;
  Session?: string;
  AuthenticationResult?: CognitoResult;
  // WebAuthn options are a dynamic W3C shape - kept loose on purpose.
  CredentialCreationOptions?: any;
  CodeDeliveryDetails?: { Destination?: string };
}

// ── base64url ⇄ ArrayBuffer (WebAuthn options arrive base64url-encoded) ──────
const b64url = (buf: ArrayBuffer): string =>
  btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const b64urlToBuf = (s: string): ArrayBuffer => {
  const p = s.replace(/-/g, "+").replace(/_/g, "/").padEnd(s.length + ((4 - (s.length % 4)) % 4), "=");
  const bin = atob(p);
  const a = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) a[i] = bin.charCodeAt(i);
  return a.buffer;
};

const randStr = (n: number): string => {
  const a = new Uint8Array(n);
  crypto.getRandomValues(a);
  return b64url(a.buffer);
};
const sha256 = (s: string): Promise<ArrayBuffer> =>
  crypto.subtle.digest("SHA-256", new TextEncoder().encode(s));

// ── Hosted-UI PKCE (latent: no in-app path calls login(); exchange() runs in
//    boot() only if a ?code is present after a redirect) ─────────────────────
export const REDIRECT = location.origin + location.pathname;

export async function login(): Promise<void> {
  const v = randStr(48);
  const c = b64url(await sha256(v));
  sessionStorage.setItem("pkce_verifier", v);
  location.href =
    getCfg().cognitoDomain +
    "/oauth2/authorize?" +
    new URLSearchParams({
      response_type: "code",
      client_id: getCfg().cognitoClientId ?? "",
      redirect_uri: REDIRECT,
      scope: "openid email profile",
      code_challenge: c,
      code_challenge_method: "S256",
    });
}

export async function exchange(code: string): Promise<void> {
  const generation = authGeneration;
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: REDIRECT,
    client_id: getCfg().cognitoClientId ?? "",
    code_verifier: sessionStorage.getItem("pkce_verifier") ?? "",
  });
  const j = await withRequestDeadline(async (signal) => {
    const response = await fetch(getCfg().cognitoDomain + "/oauth2/token", {
      method: "POST",
      headers: { "content-type": "application/x-www-form-urlencoded" },
      body,
      signal,
    });
    if (!response.ok) throw new Error("Couldn't finish sign-in. Try again.");
    return response.json();
  }, 15_000);
  requireCurrentAuth(generation);
  if (j.access_token) sessionStorage.setItem("access_token", j.access_token);
  // The access token lasts one hour, while Cognito gives this SPA a 30-day
  // refresh token. Keep both: an old tab must be able to renew its session
  // instead of continuing to render live data while every later write gets a
  // 401. Direct password/passkey sign-in already comes through storeTokens().
  if (j.refresh_token) sessionStorage.setItem("refresh_token", j.refresh_token);
  history.replaceState({}, "", REDIRECT);
}

// ── One call to the Cognito IdP API (region parsed from cognitoDomain). Every
//    in-app auth path routes through here so wire format lives in one place. ──
const cognitoRegion = (): string =>
  (getCfg().cognitoDomain?.match(/\.auth\.([a-z0-9-]+)\.amazoncognito\.com/) || [])[1] || "ap-southeast-1";

async function cognitoIdp(target: string, body: unknown): Promise<{ r: Response; j: CognitoResponse }> {
  return withRequestDeadline(async (signal) => {
    const r = await fetch("https://cognito-idp." + cognitoRegion() + ".amazonaws.com/", {
      method: "POST",
      headers: {
        "content-type": "application/x-amz-json-1.1",
        "x-amz-target": "AWSCognitoIdentityProviderService." + target,
      },
      body: JSON.stringify(body),
      signal,
    });
    const j = (await r.json().catch((error: unknown) => {
      if (error instanceof SyntaxError) return {};
      throw error;
    })) as CognitoResponse;
    return { r, j };
  }, 15_000);
}

function storeTokens(ar: CognitoResult | undefined): boolean {
  if (!ar || !ar.AccessToken) return false;
  sessionStorage.setItem("access_token", ar.AccessToken);
  if (ar.RefreshToken) sessionStorage.setItem("refresh_token", ar.RefreshToken);
  return true;
}

// Renew the short-lived access token without interrupting the user. The web
// app client explicitly allows REFRESH_TOKEN_AUTH (terraform/cognito.tf).
// Cognito normally omits a new refresh token from this response, so
// storeTokens deliberately leaves the existing one in place when absent.
export async function refreshAccessToken(): Promise<void> {
  const generation = authGeneration;
  const refreshToken = sessionStorage.getItem("refresh_token");
  if (!refreshToken) throw new SessionExpiredError();
  const { r, j } = await cognitoIdp("InitiateAuth", {
    AuthFlow: "REFRESH_TOKEN_AUTH",
    ClientId: getCfg().cognitoClientId,
    AuthParameters: { REFRESH_TOKEN: refreshToken },
  });
  requireCurrentAuth(generation);
  if (refreshToken !== sessionStorage.getItem("refresh_token")) throw new DOMException("Session changed", "AbortError");
  if (!r.ok) {
    if (/NotAuthorizedException|UserNotFoundException/.test(j.__type ?? ""))
      throw new SessionExpiredError(j.message);
    throw new Error(j.message || "Couldn't renew your session. Try again.");
  }
  if (!storeTokens(j.AuthenticationResult))
    throw new Error("Couldn't renew your session. Try again.");
}

// Best-effort server revocation accompanies explicit local logout. Previously
// issued access JWTs still expire normally at the gateway/verifier.
export async function revokeRefreshToken(refreshToken: string): Promise<void> {
  const { r } = await cognitoIdp("RevokeToken", { ClientId: getCfg().cognitoClientId, Token: refreshToken });
  if (!r.ok) throw new Error("Refresh token revocation was unavailable");
}

// Direct sign-in without leaving the page (USER_PASSWORD_AUTH).
export async function passwordLogin(email: string, password: string): Promise<void> {
  const generation = authGeneration;
  const { r, j } = await cognitoIdp("InitiateAuth", {
    AuthFlow: "USER_PASSWORD_AUTH",
    ClientId: getCfg().cognitoClientId,
    AuthParameters: { USERNAME: email, PASSWORD: password },
  });
  requireCurrentAuth(generation);
  if (j.ChallengeName) throw new Error("This account needs an extra step (" + j.ChallengeName + ").");
  if (!r.ok || !j.AuthenticationResult) throw new Error(j.message || "Check your email and password.");
  storeTokens(j.AuthenticationResult);
}

// ── WebAuthn serializers (W3C Registration/Authentication ResponseJSON) ──────
function regCredToJSON(cred: PublicKeyCredential) {
  const r = cred.response as AuthenticatorAttestationResponse;
  return {
    id: cred.id,
    rawId: b64url(cred.rawId),
    type: cred.type,
    ...(cred.authenticatorAttachment ? { authenticatorAttachment: cred.authenticatorAttachment } : {}),
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    response: {
      clientDataJSON: b64url(r.clientDataJSON),
      attestationObject: b64url(r.attestationObject),
      transports: r.getTransports ? r.getTransports() : [],
    },
  };
}
function authCredToJSON(cred: PublicKeyCredential) {
  const r = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: b64url(cred.rawId),
    type: cred.type,
    ...(cred.authenticatorAttachment ? { authenticatorAttachment: cred.authenticatorAttachment } : {}),
    clientExtensionResults: cred.getClientExtensionResults ? cred.getClientExtensionResults() : {},
    response: {
      clientDataJSON: b64url(r.clientDataJSON),
      authenticatorData: b64url(r.authenticatorData),
      signature: b64url(r.signature),
      userHandle: r.userHandle ? b64url(r.userHandle) : null,
    },
  };
}

// Enroll a passkey for the already-signed-in user. Needs an access token carrying
// aws.cognito.signin.user.admin - the in-app InitiateAuth paths mint exactly that.
export async function registerPasskey(): Promise<void> {
  const generation = currentAuthGeneration();
  const token = sessionStorage.getItem("access_token");
  if (!token) throw new Error("Sign in first, then add a passkey.");
  const start = await cognitoIdp("StartWebAuthnRegistration", { AccessToken: token });
  requireCurrentAuth(generation);
  if (!start.r.ok || !start.j.CredentialCreationOptions)
    throw new Error(start.j.message || "Couldn't start passkey setup.");
  const opts = start.j.CredentialCreationOptions; // dynamic W3C object
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...opts,
    challenge: b64urlToBuf(opts.challenge),
    user: { ...opts.user, id: b64urlToBuf(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map((c: any) => ({ ...c, id: b64urlToBuf(c.id) })),
  };
  const cred = (await credentialPrompt(signal => navigator.credentials.create({ publicKey, signal }))) as PublicKeyCredential | null;
  requireCurrentAuth(generation);
  if (!cred) throw new Error("Passkey setup was cancelled.");
  const done = await cognitoIdp("CompleteWebAuthnRegistration", {
    AccessToken: token,
    Credential: regCredToJSON(cred),
  });
  requireCurrentAuth(generation);
  if (!done.r.ok) throw new Error(done.j.message || "Couldn't finish passkey setup.");
}

// Sign in with a passkey, no password (USER_AUTH → WEB_AUTHN challenge).
export async function passkeyLogin(email: string): Promise<void> {
  const generation = authGeneration;
  const init = await cognitoIdp("InitiateAuth", {
    AuthFlow: "USER_AUTH",
    ClientId: getCfg().cognitoClientId,
    AuthParameters: { USERNAME: email, PREFERRED_CHALLENGE: "WEB_AUTHN" },
  });
  requireCurrentAuth(generation);
  if (!init.r.ok) throw new Error(init.j.message || "Couldn't start passkey sign-in.");
  const reqOpts =
    init.j.ChallengeName === "WEB_AUTHN" &&
    init.j.ChallengeParameters &&
    init.j.ChallengeParameters["CREDENTIAL_REQUEST_OPTIONS"];
  if (!reqOpts)
    throw new Error("No passkey is set up for this account. Sign in with your password, then add one.");
  const opts = JSON.parse(reqOpts); // STRING → object
  const publicKey: PublicKeyCredentialRequestOptions = {
    challenge: b64urlToBuf(opts.challenge),
    timeout: Math.min(Number(opts.timeout) || 60_000, 60_000),
    rpId: opts.rpId,
    userVerification: opts.userVerification || "preferred",
    allowCredentials: (opts.allowCredentials || []).map((c: any) => ({
      type: c.type,
      id: b64urlToBuf(c.id),
      ...(c.transports && c.transports.length ? { transports: c.transports } : {}),
    })),
  };
  const cred = (await credentialPrompt(signal => navigator.credentials.get({ publicKey, signal }))) as PublicKeyCredential | null;
  requireCurrentAuth(generation);
  if (!cred) throw new Error("Passkey sign-in was cancelled.");
  const username = (init.j.ChallengeParameters && init.j.ChallengeParameters["USERNAME"]) || email;
  const resp = await cognitoIdp("RespondToAuthChallenge", {
    ClientId: getCfg().cognitoClientId,
    ChallengeName: "WEB_AUTHN",
    Session: init.j.Session,
    ChallengeResponses: { USERNAME: username, CREDENTIAL: JSON.stringify(authCredToJSON(cred)) },
  });
  requireCurrentAuth(generation);
  if (!resp.r.ok || !resp.j.AuthenticationResult) throw new Error(resp.j.message || "Passkey sign-in failed.");
  storeTokens(resp.j.AuthenticationResult);
}

// ── Password reset (Cognito ForgotPassword → ConfirmForgotPassword) ──────────
export async function forgotPassword(email: string): Promise<string> {
  const { r, j } = await cognitoIdp("ForgotPassword", { ClientId: getCfg().cognitoClientId, Username: email });
  if (!r.ok) throw new Error(j.message || "Couldn't start the reset. Check the email and try again.");
  return (j.CodeDeliveryDetails && j.CodeDeliveryDetails.Destination) || "";
}
export async function confirmForgotPassword(email: string, code: string, password: string): Promise<void> {
  const { r, j } = await cognitoIdp("ConfirmForgotPassword", {
    ClientId: getCfg().cognitoClientId,
    Username: email,
    ConfirmationCode: code,
    Password: password,
  });
  if (!r.ok) throw new Error(j.message || "Couldn't reset the password.");
}

// Client-side mirror of the pool password policy (min 15 + a number).
export function pwChecks(pw: string) {
  return {
    len: pw.length >= 15,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    num: /[0-9]/.test(pw),
    sym: /[^A-Za-z0-9]/.test(pw),
  };
}
export const pwOk = (pw: string): boolean => {
  const c = pwChecks(pw);
  return c.len && c.num;
};
